import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import { 
    Zap, Receipt, FileSignature, MessageSquare, Send, FileText, 
    CreditCard, FileCheck, CheckCircle2, AlertCircle, Loader2,
    Upload, Download, Trash2, Eye, Copy, ExternalLink, Lock,
    ClipboardCheck, User, Building2, Phone, Mail, MapPin, Info,
    ChevronRight, CheckSquare, AlertTriangle, Pencil, Share2, Link, Clock, X,
    Flame, Plus, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import { prospectsAPI, attachmentsAPI } from '@/lib/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';

// ============== BADGE EASY POWER ==============
const EASY_POWER_BADGES = {
    incomplet: { label: 'Incomplet', color: 'bg-slate-100 text-slate-600', icon: AlertCircle },
    pret_transmettre: { label: 'Prêt à transmettre', color: 'bg-amber-100 text-amber-700', icon: CheckCircle2 },
    transmis: { label: 'Transmis', color: 'bg-blue-100 text-blue-700', icon: Send },
    etude_recue: { label: 'Étude reçue', color: 'bg-purple-100 text-purple-700', icon: FileText },
    pret_contrat: { label: 'Prêt contrat', color: 'bg-green-100 text-green-700', icon: FileCheck },
    finalise: { label: 'Finalisé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 }
};

// ============== EASY POWER MODULE PRINCIPAL ==============
export function EasyPowerModule({ prospect, onUpdate }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [easyPowerData, setEasyPowerData] = useState({});
    
    // États du module - Électricité
    const [factures, setFactures] = useState([]);
    const [mandatStatus, setMandatStatus] = useState('non_envoye'); // non_envoye, envoye, signe
    const [mandatConfirmed, setMandatConfirmed] = useState(false);
    const [dossierTransmis, setDossierTransmis] = useState(false);
    const [etudes, setEtudes] = useState([]);
    const [analyseClient, setAnalyseClient] = useState('');
    const [ribUploaded, setRibUploaded] = useState(false);
    const [contractChecklist, setContractChecklist] = useState({
        explique: false,
        correlation: false,
        signe: false
    });
    
    // États du module - Gaz (optionnel)
    const [facturesGaz, setFacturesGaz] = useState([]);
    const [etudesGaz, setEtudesGaz] = useState([]);
    const [showGazSection, setShowGazSection] = useState(false);

    // Charger les données
    useEffect(() => {
        if (prospect) {
            loadEasyPowerData();
        }
    }, [prospect]);

    const loadEasyPowerData = async () => {
        try {
            const response = await api.get(`/prospects/${prospect.id}/easy-power-module`);
            const data = response.data;
            
            setEasyPowerData(data);
            setFactures(data.factures || []);
            setMandatStatus(data.mandat_status || 'non_envoye');
            setMandatConfirmed(data.mandat_confirmed || false);
            setDossierTransmis(data.dossier_transmis || false);
            setEtudes(data.etudes || []);
            setAnalyseClient(data.analyse_client || '');
            setRibUploaded(!!data.rib_file_path);
            setContractChecklist(data.contract_checklist || {
                explique: false,
                correlation: false,
                signe: false
            });
            
            // Gaz (optionnel)
            setFacturesGaz(data.factures_gaz || []);
            setEtudesGaz(data.etudes_gaz || []);
            // Afficher la section gaz si des données existent
            setShowGazSection((data.factures_gaz?.length > 0) || (data.etudes_gaz?.length > 0));
        } catch (error) {
            console.error('Erreur chargement module Easy Power:', error);
            // Fallback sur les données prospect existantes
            if (prospect?.attachments) {
                setFactures(prospect.attachments.filter(a => a.attachment_type === 'FACTURE'));
            }
            if (prospect?.rgpd_mandat?.realise) {
                setMandatStatus('signe');
                setMandatConfirmed(true);
            }
            if (prospect?.easy_power_data) {
                setRibUploaded(!!prospect.easy_power_data.rib_file_path);
                setAnalyseClient(prospect.easy_power_data.etude || '');
            }
        }
    };

    // Calculer le badge Easy Power
    const getEasyPowerBadge = () => {
        if (contractChecklist.signe) return 'finalise';
        if (ribUploaded) return 'pret_contrat';
        if (etudes.length > 0 || prospect?.easy_power_data?.etude_file_path) return 'etude_recue';
        if (dossierTransmis) return 'transmis';
        if (factures.length > 0 && mandatConfirmed) return 'pret_transmettre';
        return 'incomplet';
    };

    const badge = getEasyPowerBadge();
    const BadgeIcon = EASY_POWER_BADGES[badge].icon;
    
    // État Multi-PDL
    const [multiPdlEnabled, setMultiPdlEnabled] = useState(false);
    const [pdls, setPdls] = useState([]);
    const [activePdl, setActivePdl] = useState('main');
    const [togglingMultiPdl, setTogglingMultiPdl] = useState(false);
    const { isAdminFull, isAdmin, isVP, isApporteur } = useAuth();
    // Tout le monde peut gérer le Multi-PDL (Admin, VP, Apporteur)
    const canManageMultiPdl = true;
    
    // Charger l'état Multi-PDL
    useEffect(() => {
        if (prospect?.easy_power_data) {
            setMultiPdlEnabled(prospect.easy_power_data.multi_pdl_enabled || false);
            setPdls(prospect.easy_power_data.pdls || []);
        }
    }, [prospect?.easy_power_data]);
    
    // Toggle Multi-PDL
    const handleToggleMultiPdl = async (enabled) => {
        setTogglingMultiPdl(true);
        try {
            const response = await api.put(`/prospects/${prospect.id}/easy-power/multi-pdl`, { enabled });
            setMultiPdlEnabled(response.data.multi_pdl_enabled);
            setPdls(response.data.pdls || []);
            toast.success(enabled ? 'Mode Multi-PDL activé' : 'Mode Multi-PDL désactivé');
            loadEasyPowerData();
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setTogglingMultiPdl(false);
        }
    };
    
    // Ajouter un PDL
    const handleAddPdl = async () => {
        try {
            const response = await api.post(`/prospects/${prospect.id}/easy-power/pdls`, {
                pdl_number: '',
                raison_sociale: `PDL ${pdls.length + 1}`
            });
            setPdls(prev => [...prev, response.data.pdl]);
            setActivePdl(response.data.pdl.id);
            toast.success('Nouveau PDL ajouté');
            loadEasyPowerData();
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    // Supprimer un PDL
    const handleDeletePdl = async (pdlId) => {
        if (!window.confirm('Supprimer ce PDL et toutes ses données ?')) return;
        try {
            await api.delete(`/prospects/${prospect.id}/easy-power/pdls/${pdlId}`);
            setPdls(prev => prev.filter(p => p.id !== pdlId));
            if (activePdl === pdlId) setActivePdl('main');
            toast.success('PDL supprimé');
            loadEasyPowerData();
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    // Ne pas afficher si pas de pipeline Easy Power
    if (!prospect?.easy_power_stage) {
        return null;
    }
    
    // Composant de workflow complet pour un PDL donné
    const renderPdlWorkflow = (pdlIndex = null, pdlData = null) => (
        <div className="space-y-6">
            {/* ÉTAPE 1 : Factures Électricité */}
            <FacturesElectriciteStep 
                prospectId={prospect.id}
                factures={factures}
                onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                canProceed={factures.length > 0}
            />

            {/* SECTION GAZ (Optionnelle) */}
            <GazSection
                prospectId={prospect.id}
                facturesGaz={facturesGaz}
                etudesGaz={etudesGaz}
                showGazSection={showGazSection}
                setShowGazSection={setShowGazSection}
                onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
            />

            {/* ÉTAPE 2 : Mandat */}
            <MandatStep
                prospect={prospect}
                mandatStatus={mandatStatus}
                mandatConfirmed={mandatConfirmed}
                setMandatStatus={setMandatStatus}
                setMandatConfirmed={setMandatConfirmed}
                onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                canProceed={mandatConfirmed}
                factures={factures}
            />

            {/* ÉTAPE 3 : Bloc Transmission WhatsApp */}
            <WhatsAppTransmissionStep
                prospect={prospect}
                canCopy={mandatConfirmed && factures.length > 0}
            />

            {/* ÉTAPE 4 : Dossier Transmis */}
            <DossierTransmisStep
                prospectId={prospect.id}
                dossierTransmis={dossierTransmis}
                setDossierTransmis={setDossierTransmis}
                mandatConfirmed={mandatConfirmed}
                facturesCount={factures.length}
                onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
            />

            {/* ÉTAPE 5 : Étude */}
            <EtudeStep
                prospect={prospect}
                etudes={etudes}
                analyseClient={analyseClient}
                setAnalyseClient={setAnalyseClient}
                onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                isAdmin={user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true}
            />

            {/* ÉTAPE 6 : Présentation Client */}
            <PresentationClientStep
                prospect={prospect}
                factures={factures}
                etudes={etudes}
            />

            {/* ÉTAPE 7 : Upload RIB */}
            <RibUploadStep
                prospect={prospect}
                ribUploaded={ribUploaded}
                onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
            />

            {/* ÉTAPE 8 : Rappel Signature Contrat */}
            <SignatureContratStep
                prospectId={prospect.id}
                checklist={contractChecklist}
                setChecklist={setContractChecklist}
                onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
            />
        </div>
    );

    return (
        <div className="space-y-6" data-testid="easy-power-module">
            {/* En-tête avec Badge et Toggle Multi-PDL */}
            <Card className="border-2 border-amber-400 bg-gradient-to-r from-amber-50 to-orange-50">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                                <Zap className="w-7 h-7 text-amber-600" />
                            </div>
                            <div>
                                <CardTitle className="text-xl text-amber-800">Module Easy Power</CardTitle>
                                <CardDescription className="text-amber-600">
                                    Parcours structuré pour le dossier électricité
                                </CardDescription>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {/* Toggle Multi-PDL */}
                            {canManageMultiPdl && (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/50 border border-amber-200">
                                    <span className="text-sm text-amber-700">Multi-PDL</span>
                                    <Switch
                                        checked={multiPdlEnabled}
                                        onCheckedChange={handleToggleMultiPdl}
                                        disabled={togglingMultiPdl}
                                        data-testid="toggle-multi-pdl"
                                    />
                                </div>
                            )}
                            <Badge className={cn("text-sm px-3 py-1", EASY_POWER_BADGES[badge].color)} data-testid="easy-power-badge">
                                <BadgeIcon className="w-4 h-4 mr-1.5" />
                                {EASY_POWER_BADGES[badge].label}
                            </Badge>
                        </div>
                    </div>
                </CardHeader>
            </Card>

            {/* INFORMATIONS EASY POWER (Modifiables) - Commun à tous les PDLs */}
            <EasyPowerInfoStep
                prospect={prospect}
                onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
            />

            {/* ============== MODE MULTI-PDL ============== */}
            {multiPdlEnabled ? (
                <div className="space-y-4">
                    {/* Barre de contrôle Multi-PDL */}
                    <Card className="border-yellow-300 bg-yellow-50/50">
                        <CardContent className="py-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Badge className="bg-yellow-500 text-white">
                                        {pdls.length} PDL{pdls.length > 1 ? 's' : ''}
                                    </Badge>
                                    <span className="text-sm text-yellow-700">
                                        Mode Multi-PDL activé - Chaque PDL a son propre workflow complet
                                    </span>
                                </div>
                                {canManageMultiPdl && (
                                    <Button
                                        size="sm"
                                        onClick={handleAddPdl}
                                        className="bg-yellow-500 hover:bg-yellow-600"
                                        data-testid="add-pdl-btn"
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Ajouter PDL
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Accordéon des PDLs - Chaque PDL a le workflow complet */}
                    {pdls.length === 0 ? (
                        <Card className="border-dashed">
                            <CardContent className="py-8 text-center text-slate-500">
                                <Zap className="w-10 h-10 mx-auto mb-3 opacity-30" />
                                <p>Aucun PDL configuré</p>
                                <p className="text-sm">Cliquez sur "Ajouter PDL" pour créer votre premier point de livraison</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <Accordion 
                            type="single" 
                            collapsible 
                            value={activePdl}
                            onValueChange={setActivePdl}
                            className="space-y-3"
                        >
                            {pdls.map((pdl, index) => (
                                <AccordionItem
                                    key={pdl.id}
                                    value={pdl.id}
                                    className="border-2 border-amber-200 rounded-lg bg-white overflow-hidden"
                                >
                                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-amber-50/50">
                                        <div className="flex items-center justify-between w-full pr-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                                                    {index + 1}
                                                </div>
                                                <div className="text-left">
                                                    <p className="font-semibold text-amber-900">
                                                        {pdl.raison_sociale || `PDL ${index + 1}`}
                                                    </p>
                                                    {pdl.pdl_number && (
                                                        <p className="text-xs text-amber-600">
                                                            N° PDL: {pdl.pdl_number}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {pdl.is_default && (
                                                    <Badge variant="outline" className="border-amber-300 text-amber-700">
                                                        Principal
                                                    </Badge>
                                                )}
                                                {pdl.contract_signed ? (
                                                    <Badge className="bg-emerald-100 text-emerald-700">
                                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                                        Contrat signé
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-slate-500">
                                                        En cours
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-4 pb-4">
                                        {/* Section d'identification du PDL */}
                                        <div className="mb-4 pt-2 border-t">
                                            <div className="grid grid-cols-2 gap-4 py-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-amber-600">Nom du PDL</Label>
                                                    <Input
                                                        value={pdl.raison_sociale || ''}
                                                        onChange={async (e) => {
                                                            const newName = e.target.value;
                                                            try {
                                                                await api.put(`/prospects/${prospect.id}/easy-power/pdls/${pdl.id}`, {
                                                                    raison_sociale: newName
                                                                });
                                                                setPdls(prev => prev.map(p => 
                                                                    p.id === pdl.id ? { ...p, raison_sociale: newName } : p
                                                                ));
                                                            } catch (err) {
                                                                console.error('Erreur mise à jour nom PDL:', err);
                                                            }
                                                        }}
                                                        placeholder="Ex: Compteur Bâtiment A"
                                                        className="h-8 text-sm"
                                                        data-testid={`pdl-name-${index}`}
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs text-amber-600">N° PDL (14 chiffres)</Label>
                                                    <Input
                                                        value={pdl.pdl_number || ''}
                                                        onChange={async (e) => {
                                                            const newNumber = e.target.value;
                                                            try {
                                                                await api.put(`/prospects/${prospect.id}/easy-power/pdls/${pdl.id}`, {
                                                                    pdl_number: newNumber
                                                                });
                                                                setPdls(prev => prev.map(p => 
                                                                    p.id === pdl.id ? { ...p, pdl_number: newNumber } : p
                                                                ));
                                                            } catch (err) {
                                                                console.error('Erreur mise à jour numéro PDL:', err);
                                                            }
                                                        }}
                                                        placeholder="Ex: 12345678901234"
                                                        className="h-8 text-sm font-mono"
                                                        data-testid={`pdl-number-${index}`}
                                                    />
                                                </div>
                                            </div>
                                            
                                            {/* Bouton supprimer pour les PDLs non-principaux */}
                                            {!pdl.is_default && canManageMultiPdl && (
                                                <div className="flex justify-end">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleDeletePdl(pdl.id)}
                                                        className="text-red-600 border-red-200 hover:bg-red-50"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-1" />
                                                        Supprimer ce PDL
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Workflow complet pour ce PDL */}
                                        {renderPdlWorkflow(index, pdl)}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    )}
                </div>
            ) : (
                /* ============== MODE NORMAL (UN SEUL PDL) ============== */
                <>
                    {/* ÉTAPE 1 : Factures Électricité */}
                    <FacturesElectriciteStep 
                        prospectId={prospect.id}
                        factures={factures}
                        onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                        canProceed={factures.length > 0}
                    />

                    {/* SECTION GAZ (Optionnelle) */}
                    <GazSection
                        prospectId={prospect.id}
                        facturesGaz={facturesGaz}
                        etudesGaz={etudesGaz}
                        showGazSection={showGazSection}
                        setShowGazSection={setShowGazSection}
                        onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                    />

                    {/* ÉTAPE 2 : Mandat */}
                    <MandatStep
                        prospect={prospect}
                        mandatStatus={mandatStatus}
                        mandatConfirmed={mandatConfirmed}
                        setMandatStatus={setMandatStatus}
                        setMandatConfirmed={setMandatConfirmed}
                        onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                        canProceed={mandatConfirmed}
                        factures={factures}
                    />

                    {/* ÉTAPE 3 : Bloc Transmission WhatsApp */}
                    <WhatsAppTransmissionStep
                        prospect={prospect}
                        canCopy={mandatConfirmed && factures.length > 0}
                    />

                    {/* ÉTAPE 4 : Dossier Transmis */}
                    <DossierTransmisStep
                        prospectId={prospect.id}
                        dossierTransmis={dossierTransmis}
                        setDossierTransmis={setDossierTransmis}
                        mandatConfirmed={mandatConfirmed}
                        facturesCount={factures.length}
                        onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                    />

                    {/* ÉTAPE 5 : Étude */}
                    <EtudeStep
                        prospect={prospect}
                        etudes={etudes}
                        analyseClient={analyseClient}
                        setAnalyseClient={setAnalyseClient}
                        onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                        isAdmin={user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true}
                    />

                    {/* ÉTAPE 6 : Présentation Client */}
                    <PresentationClientStep
                        prospect={prospect}
                        factures={factures}
                        etudes={etudes}
                    />

                    {/* ÉTAPE 7 : Upload RIB */}
                    <RibUploadStep
                        prospect={prospect}
                        ribUploaded={ribUploaded}
                        onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                    />

                    {/* ÉTAPE 8 : Rappel Signature Contrat */}
                    <SignatureContratStep
                        prospectId={prospect.id}
                        checklist={contractChecklist}
                        setChecklist={setContractChecklist}
                        onUpdate={() => { loadEasyPowerData(); onUpdate?.(); }}
                    />
                </>
            )}
        </div>
    );
}

// ============== INFORMATIONS EASY POWER (Modifiables) ==============
function EasyPowerInfoStep({ prospect, onUpdate }) {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [preFilling, setPreFilling] = useState(false);
    
    const ep = prospect.easy_power_data || {};
    const contact = ep.contact || {};
    const gerant = ep.gerant || {};
    
    const [formData, setFormData] = useState({
        raison_sociale: ep.raison_sociale || '',
        siren: ep.siren || '',
        adresse_contrat: ep.adresse_contrat || '',
        code_postal: ep.code_postal || '',
        ville: ep.ville || '',
        contact_nom: contact.nom || '',
        contact_prenom: contact.prenom || '',
        contact_email: contact.email || '',
        contact_telephone: contact.telephone || '',
        gerant_nom: gerant.nom || '',
        gerant_prenom: gerant.prenom || ''
    });

    // Mettre à jour le formulaire quand prospect change
    useEffect(() => {
        const ep = prospect.easy_power_data || {};
        const contact = ep.contact || {};
        const gerant = ep.gerant || {};
        setFormData({
            raison_sociale: ep.raison_sociale || '',
            siren: ep.siren || '',
            adresse_contrat: ep.adresse_contrat || '',
            code_postal: ep.code_postal || '',
            ville: ep.ville || '',
            contact_nom: contact.nom || '',
            contact_prenom: contact.prenom || '',
            contact_email: contact.email || '',
            contact_telephone: contact.telephone || '',
            gerant_nom: gerant.nom || '',
            gerant_prenom: gerant.prenom || ''
        });
    }, [prospect]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handlePreFill = async () => {
        setPreFilling(true);
        try {
            await api.post(`/prospects/${prospect.id}/easy-power-data/auto-fill`);
            toast.success('Informations pré-remplies depuis la fiche prospect');
            onUpdate?.();
        } catch (error) {
            toast.error('Erreur lors du pré-remplissage');
        } finally {
            setPreFilling(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = {
                raison_sociale: formData.raison_sociale,
                siren: formData.siren,
                adresse_contrat: formData.adresse_contrat,
                code_postal: formData.code_postal,
                ville: formData.ville,
                contact: {
                    nom: formData.contact_nom,
                    prenom: formData.contact_prenom,
                    email: formData.contact_email,
                    telephone: formData.contact_telephone
                },
                gerant: {
                    nom: formData.gerant_nom,
                    prenom: formData.gerant_prenom
                }
            };
            await api.put(`/prospects/${prospect.id}/easy-power-data`, data);
            toast.success('Informations enregistrées');
            setIsEditing(false);
            onUpdate?.();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // Vérifier si des données sont manquantes pour afficher l'alerte
    const hasMissingData = !formData.raison_sociale || !formData.siren || !formData.contact_nom;

    return (
        <Card className="border-2 border-blue-200 bg-blue-50/30" data-testid="step-info-ep">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white">
                            <Building2 className="w-4 h-4" />
                        </div>
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Info className="w-5 h-5" />
                                Informations Easy Power
                            </CardTitle>
                            <CardDescription>
                                Données utilisées pour le message WhatsApp et le contrat
                            </CardDescription>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handlePreFill}
                            disabled={preFilling}
                            data-testid="prefill-ep-btn"
                        >
                            {preFilling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                            Pré-remplir
                        </Button>
                        {!isEditing ? (
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => setIsEditing(true)}
                                data-testid="edit-ep-info-btn"
                            >
                                <Pencil className="w-4 h-4 mr-1" />
                                Modifier
                            </Button>
                        ) : (
                            <Button 
                                size="sm"
                                onClick={handleSave}
                                disabled={saving}
                                data-testid="save-ep-info-btn"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                                Enregistrer
                            </Button>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {hasMissingData && !isEditing && (
                    <Alert className="bg-amber-50 border-amber-200">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-700 text-sm">
                            Certaines informations sont manquantes. Cliquez sur <strong>"Pré-remplir"</strong> pour les compléter automatiquement ou <strong>"Modifier"</strong> pour les saisir manuellement.
                        </AlertDescription>
                    </Alert>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Société */}
                    <div className="space-y-3 p-4 bg-white rounded-lg border">
                        <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            Société
                        </h4>
                        {isEditing ? (
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-xs">Raison sociale</Label>
                                    <Input
                                        value={formData.raison_sociale}
                                        onChange={(e) => handleChange('raison_sociale', e.target.value)}
                                        placeholder="Nom de l'entreprise"
                                        data-testid="ep-raison-sociale"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">SIREN</Label>
                                    <Input
                                        value={formData.siren}
                                        onChange={(e) => handleChange('siren', e.target.value)}
                                        placeholder="123456789"
                                        maxLength={9}
                                        data-testid="ep-siren"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2 text-sm">
                                <p><span className="text-slate-500">Raison sociale:</span> <strong>{formData.raison_sociale || <span className="text-red-500">Non renseigné</span>}</strong></p>
                                <p><span className="text-slate-500">SIREN:</span> <strong>{formData.siren || <span className="text-red-500">Non renseigné</span>}</strong></p>
                                <p><span className="text-slate-500">SIRET (prospect):</span> <strong>{prospect.siret || '-'}</strong></p>
                            </div>
                        )}
                    </div>

                    {/* Adresse */}
                    <div className="space-y-3 p-4 bg-white rounded-lg border">
                        <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Adresse du contrat
                        </h4>
                        {isEditing ? (
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-xs">Adresse</Label>
                                    <Input
                                        value={formData.adresse_contrat}
                                        onChange={(e) => handleChange('adresse_contrat', e.target.value)}
                                        placeholder="123 rue..."
                                        data-testid="ep-adresse"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-xs">Code postal</Label>
                                        <Input
                                            value={formData.code_postal}
                                            onChange={(e) => handleChange('code_postal', e.target.value)}
                                            placeholder="75001"
                                            maxLength={5}
                                            data-testid="ep-cp"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Ville</Label>
                                        <Input
                                            value={formData.ville}
                                            onChange={(e) => handleChange('ville', e.target.value)}
                                            placeholder="Paris"
                                            data-testid="ep-ville"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm">
                                <p>{formData.adresse_contrat || <span className="text-slate-400">Non renseigné</span>}</p>
                                <p>{formData.code_postal} {formData.ville}</p>
                            </div>
                        )}
                    </div>

                    {/* Contact principal */}
                    <div className="space-y-3 p-4 bg-white rounded-lg border">
                        <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Contact principal
                        </h4>
                        {isEditing ? (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="text-xs">Nom</Label>
                                        <Input
                                            value={formData.contact_nom}
                                            onChange={(e) => handleChange('contact_nom', e.target.value)}
                                            placeholder="Dupont"
                                            data-testid="ep-contact-nom"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Prénom</Label>
                                        <Input
                                            value={formData.contact_prenom}
                                            onChange={(e) => handleChange('contact_prenom', e.target.value)}
                                            placeholder="Jean"
                                            data-testid="ep-contact-prenom"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs">Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.contact_email}
                                        onChange={(e) => handleChange('contact_email', e.target.value)}
                                        placeholder="email@exemple.com"
                                        data-testid="ep-contact-email"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Téléphone</Label>
                                    <Input
                                        value={formData.contact_telephone}
                                        onChange={(e) => handleChange('contact_telephone', e.target.value)}
                                        placeholder="06 12 34 56 78"
                                        data-testid="ep-contact-tel"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-1 text-sm">
                                <p><strong>{formData.contact_prenom} {formData.contact_nom || <span className="text-red-500">Non renseigné</span>}</strong></p>
                                <p className="flex items-center gap-1"><Mail className="w-3 h-3" /> {formData.contact_email || '-'}</p>
                                <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {formData.contact_telephone || '-'}</p>
                            </div>
                        )}
                    </div>

                    {/* Gérant */}
                    <div className="space-y-3 p-4 bg-white rounded-lg border">
                        <h4 className="font-medium text-sm text-slate-700 flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Gérant (si différent)
                        </h4>
                        {isEditing ? (
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-xs">Nom</Label>
                                    <Input
                                        value={formData.gerant_nom}
                                        onChange={(e) => handleChange('gerant_nom', e.target.value)}
                                        placeholder="Nom du gérant"
                                        data-testid="ep-gerant-nom"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Prénom</Label>
                                    <Input
                                        value={formData.gerant_prenom}
                                        onChange={(e) => handleChange('gerant_prenom', e.target.value)}
                                        placeholder="Prénom du gérant"
                                        data-testid="ep-gerant-prenom"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm">
                                {formData.gerant_nom || formData.gerant_prenom ? (
                                    <p><strong>{formData.gerant_prenom} {formData.gerant_nom}</strong></p>
                                ) : (
                                    <p className="text-slate-400 italic">Identique au contact principal</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {isEditing && (
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                            Annuler
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ============== ÉTAPE 1 : FACTURES ÉLECTRICITÉ ==============
function FacturesElectriciteStep({ prospectId, factures, onUpdate, canProceed }) {
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const fileInputRef = useRef(null);
    const { user } = useAuth();

    const handleUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        
        setUploading(true);
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('attachment_type', 'FACTURE');
                await prospectsAPI.uploadAttachment(prospectId, formData);
            }
            toast.success(files.length > 1 ? `${files.length} factures ajoutées` : 'Facture ajoutée');
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (attachmentId) => {
        // Vérifier les permissions
        const attachment = factures.find(f => f.id === attachmentId);
        if (!attachment) return;
        
        const canDelete = (user?.role === 'admin_full' || user?.is_admin === true) || attachment.uploaded_by_id === user?.id;
        if (!canDelete) {
            toast.error('Seul l\'utilisateur ayant uploadé ce fichier peut le supprimer');
            return;
        }
        
        if (!window.confirm('Supprimer cette facture ?')) return;
        
        setDeleting(attachmentId);
        try {
            await attachmentsAPI.delete(attachmentId);
            toast.success('Facture supprimée');
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setDeleting(null);
        }
    };

    const handleDownload = async (attachment) => {
        try {
            const response = await attachmentsAPI.download(attachment.id);
            const blob = new Blob([response.data], { type: attachment.mime_type });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.original_filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Erreur lors du téléchargement');
        }
    };

    return (
        <Card className={cn(
            "border-2 transition-all",
            canProceed ? "border-green-300 bg-green-50/50" : "border-amber-300 bg-amber-50/50"
        )} data-testid="step-factures">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                            canProceed ? "bg-green-500" : "bg-amber-500"
                        )}>
                            1
                        </div>
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <Receipt className="w-5 h-5" />
                                Factures Électricité
                                {canProceed && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                            </CardTitle>
                            <CardDescription>
                                {factures.length > 0 
                                    ? `${factures.length} facture(s) uploadée(s)`
                                    : "Au moins 1 facture requise pour transmettre le dossier"}
                            </CardDescription>
                        </div>
                    </div>
                    <div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={handleUpload}
                            className="hidden"
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            size="sm"
                            data-testid="upload-facture-btn"
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                            Ajouter
                        </Button>
                    </div>
                </div>
            </CardHeader>
            {factures.length > 0 && (
                <CardContent>
                    <div className="space-y-2">
                        {factures.map((facture) => (
                            <div 
                                key={facture.id}
                                className="flex items-center justify-between p-3 bg-white rounded-lg border"
                            >
                                <div className="flex items-center gap-3">
                                    <FileText className="w-5 h-5 text-amber-600" />
                                    <div>
                                        <p className="font-medium text-sm truncate max-w-[200px]">{facture.original_filename}</p>
                                        <p className="text-xs text-slate-500">
                                            {format(new Date(facture.created_at), "dd MMM yyyy", { locale: fr })}
                                            {facture.uploaded_by_name && ` • ${facture.uploaded_by_name}`}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={() => {
                                            const token = localStorage.getItem('valnexo_token');
                                            const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/attachments/${facture.id}/view?token=${token}`;
                                            window.open(url, '_blank');
                                        }}
                                        title="Visualiser"
                                    >
                                        <Eye className="w-4 h-4 text-blue-600" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDownload(facture)}>
                                        <Download className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={() => handleDelete(facture.id)}
                                        disabled={deleting === facture.id}
                                        className="text-red-600 hover:bg-red-50"
                                    >
                                        {deleting === facture.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            )}
        </Card>
    );
}

// ============== ÉTAPE 2 : MANDAT ==============
function MandatStep({ prospect, mandatStatus, mandatConfirmed, setMandatStatus, setMandatConfirmed, onUpdate, canProceed, factures = [] }) {
    const [saving, setSaving] = useState(false);
    const YOUSIGN_URL = "https://yousign.app/workflows/forms/538d6c8e-fe5b-42e4-90ea-f6cc4bd85154";

    const handleStatusChange = async (newStatus) => {
        setSaving(true);
        try {
            await api.put(`/prospects/${prospect.id}/easy-power-mandat`, {
                mandat_status: newStatus,
                mandat_confirmed: mandatConfirmed
            });
            setMandatStatus(newStatus);
            onUpdate?.();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmChange = async (checked) => {
        setSaving(true);
        try {
            await api.put(`/prospects/${prospect.id}/easy-power-mandat`, {
                mandat_status: checked ? 'signe' : mandatStatus,
                mandat_confirmed: checked
            });
            setMandatConfirmed(checked);
            if (checked) setMandatStatus('signe');
            toast.success(checked ? 'Mandat confirmé signé' : 'Confirmation retirée');
            onUpdate?.();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };

    // Ouvrir le mandat ET les factures en même temps
    const handleOpenMandatWithFactures = () => {
        // Ouvrir le mandat d'abord
        window.open(YOUSIGN_URL, '_blank');
        
        // Ouvrir les factures pour avoir le PDL
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        factures.forEach((facture, index) => {
            setTimeout(() => {
                const url = `${baseUrl}/api/attachments/${facture.id}/download`;
                window.open(url, `facture_${facture.id}`, 'noopener,noreferrer');
            }, (index + 1) * 300);
        });
        
        if (factures.length > 0) {
            toast.success(`Mandat + ${factures.length} facture(s) ouverts - utilisez le PDL de la facture pour remplir le mandat`);
        }
    };

    const statusColors = {
        non_envoye: 'bg-slate-100 text-slate-600',
        envoye: 'bg-blue-100 text-blue-700',
        signe: 'bg-green-100 text-green-700'
    };

    const statusLabels = {
        non_envoye: 'Non envoyé',
        envoye: 'Envoyé',
        signe: 'Signé'
    };

    return (
        <Card className={cn(
            "border-2 transition-all",
            canProceed ? "border-green-300 bg-green-50/50" : "border-indigo-300 bg-indigo-50/50"
        )} data-testid="step-mandat">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                            canProceed ? "bg-green-500" : "bg-indigo-500"
                        )}>
                            2
                        </div>
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <FileSignature className="w-5 h-5" />
                                Mandat
                                <Badge className={statusColors[mandatStatus]}>{statusLabels[mandatStatus]}</Badge>
                                {canProceed && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                            </CardTitle>
                            <CardDescription>
                                Le mandat doit être signé avant de transmettre le dossier
                            </CardDescription>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Bouton principal : ouvre mandat + factures ensemble */}
                        <Button
                            onClick={handleOpenMandatWithFactures}
                            variant={factures.length > 0 ? "default" : "outline"}
                            className={factures.length > 0 ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                            data-testid="open-mandat-btn"
                        >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            {factures.length > 0 ? (
                                <>Ouvrir mandat + facture{factures.length > 1 ? 's' : ''}</>
                            ) : (
                                <>Ouvrir le mandat</>
                            )}
                        </Button>
                        {factures.length > 0 && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                                <FileText className="w-3 h-3 mr-1" />
                                {factures.length} facture{factures.length > 1 ? 's' : ''} pour le PDL
                            </Badge>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Statut interne */}
                <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium">Statut :</Label>
                    <div className="flex gap-2">
                        {['non_envoye', 'envoye', 'signe'].map((status) => (
                            <Button
                                key={status}
                                variant={mandatStatus === status ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => handleStatusChange(status)}
                                disabled={saving}
                                className={mandatStatus === status ? '' : 'opacity-60'}
                            >
                                {statusLabels[status]}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Case confirmation obligatoire */}
                <div className={cn(
                    "flex items-center space-x-3 p-4 rounded-lg border-2",
                    mandatConfirmed 
                        ? "bg-green-50 border-green-300" 
                        : "bg-amber-50 border-amber-300"
                )}>
                    <Checkbox
                        id="mandat-confirme"
                        checked={mandatConfirmed}
                        onCheckedChange={handleConfirmChange}
                        disabled={saving}
                        data-testid="mandat-confirme-checkbox"
                    />
                    <div className="grid gap-1.5 leading-none">
                        <label htmlFor="mandat-confirme" className="text-sm font-medium leading-none cursor-pointer">
                            Je confirme que le mandat est signé
                        </label>
                        <p className="text-xs text-slate-500">
                            Cette confirmation est obligatoire pour transmettre le dossier
                        </p>
                    </div>
                </div>

                {/* Message bloquant si non signé */}
                {!mandatConfirmed && (
                    <Alert variant="destructive" className="bg-red-50 border-red-200">
                        <Lock className="h-4 w-4" />
                        <AlertTitle>Transmission impossible</AlertTitle>
                        <AlertDescription>
                            Le mandat doit être signé et confirmé pour pouvoir transmettre le dossier.
                        </AlertDescription>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
}

// ============== ÉTAPE 3 : BLOC WHATSAPP ==============
function WhatsAppTransmissionStep({ prospect, canCopy }) {
    const [copied, setCopied] = useState(false);

    // Générer le texte à copier
    const generateWhatsAppText = () => {
        const ep = prospect.easy_power_data || {};
        const contact = ep.contact || {};
        const gerant = ep.gerant || {};
        
        const gerantInfo = (gerant.prenom || gerant.nom) 
            ? `${gerant.prenom || ''} ${gerant.nom || ''}`.trim()
            : "identique au contact principal";

        return `Je confirme que le mandat a été envoyé et signé.

Société: ${ep.raison_sociale || prospect.company_name || '-'}
SIREN / SIRET: ${ep.siren || prospect.siren || '-'} / ${prospect.siret || '-'}
Adresse: ${ep.adresse_contrat || prospect.address || '-'}, ${ep.code_postal || prospect.postal_code || ''} ${ep.ville || prospect.city || ''}

Contact principal:
- Nom: ${contact.nom || prospect.last_name || '-'} ${contact.prenom || prospect.first_name || ''}
- Tél: ${contact.telephone || prospect.phone || '-'}
- Email: ${contact.email || prospect.email || '-'}

Gérant: ${gerantInfo}`;
    };

    const handleCopy = () => {
        if (!canCopy) {
            toast.error('Le mandat doit être signé pour copier le message');
            return;
        }
        
        navigator.clipboard.writeText(generateWhatsAppText());
        setCopied(true);
        toast.success('Message copié !');
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Card className={cn(
            "border-2 transition-all",
            canCopy ? "border-green-300 bg-green-50/50" : "border-slate-300 bg-slate-50/50"
        )} data-testid="step-whatsapp">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                    <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                        canCopy ? "bg-green-500" : "bg-slate-400"
                    )}>
                        3
                    </div>
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <MessageSquare className="w-5 h-5" />
                            Transmission WhatsApp
                        </CardTitle>
                        <CardDescription>
                            Copiez ce message pour le groupe WhatsApp
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Zone texte readonly */}
                <div className={cn(
                    "p-4 rounded-lg border font-mono text-sm whitespace-pre-wrap",
                    canCopy ? "bg-white border-green-200" : "bg-slate-100 border-slate-200 opacity-60"
                )}>
                    {generateWhatsAppText()}
                </div>

                {/* Bouton Copier */}
                <Button
                    onClick={handleCopy}
                    disabled={!canCopy}
                    className={cn(
                        "w-full",
                        canCopy 
                            ? copied ? "bg-green-600" : "bg-green-500 hover:bg-green-600"
                            : "bg-slate-300"
                    )}
                    data-testid="copy-whatsapp-btn"
                >
                    {copied ? (
                        <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Copié !
                        </>
                    ) : (
                        <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copier le message
                        </>
                    )}
                </Button>

                {/* Aide */}
                {canCopy && (
                    <Alert className="bg-blue-50 border-blue-200">
                        <Info className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-700 text-sm">
                            Après avoir collé ce message dans le groupe WhatsApp, <strong>joignez impérativement la/les facture(s)</strong>.
                            <br />
                            <span className="text-red-600 font-medium">Ne transmettez jamais un dossier si le mandat n'est pas signé.</span>
                        </AlertDescription>
                    </Alert>
                )}

                {!canCopy && (
                    <Alert variant="destructive" className="bg-red-50 border-red-200">
                        <Lock className="h-4 w-4" />
                        <AlertDescription>
                            Le bouton Copier est désactivé car le mandat n'est pas signé ou aucune facture n'a été uploadée.
                        </AlertDescription>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
}

// ============== ÉTAPE 4 : DOSSIER TRANSMIS ==============
function DossierTransmisStep({ prospectId, dossierTransmis, setDossierTransmis, mandatConfirmed, facturesCount, onUpdate }) {
    const [saving, setSaving] = useState(false);
    const [transmissionInfo, setTransmissionInfo] = useState(null);

    const canTransmit = mandatConfirmed && facturesCount > 0;

    const handleTransmit = async (checked) => {
        if (!canTransmit && checked) {
            if (!mandatConfirmed) {
                toast.error('Le mandat doit être signé');
            } else if (facturesCount === 0) {
                toast.error('Au moins une facture doit être uploadée');
            }
            return;
        }

        setSaving(true);
        try {
            const response = await api.put(`/prospects/${prospectId}/easy-power-transmis`, {
                dossier_transmis: checked
            });
            setDossierTransmis(checked);
            if (checked && response.data.transmission_info) {
                setTransmissionInfo(response.data.transmission_info);
            }
            if (checked) {
                toast.success('Dossier marqué comme transmis', {
                    description: 'Les admins ont été notifiés par email et notification.'
                });
            } else {
                toast.info('Marquage retiré');
            }
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className={cn(
            "border-2 transition-all",
            dossierTransmis ? "border-green-300 bg-green-50/50" : "border-blue-300 bg-blue-50/50"
        )} data-testid="step-dossier-transmis">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                    <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                        dossierTransmis ? "bg-green-500" : "bg-blue-500"
                    )}>
                        4
                    </div>
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            Dossier transmis
                            {dossierTransmis && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                        </CardTitle>
                        <CardDescription>
                            Confirmez la transmission au groupe WhatsApp
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className={cn(
                    "flex items-center space-x-3 p-4 rounded-lg border-2",
                    dossierTransmis 
                        ? "bg-green-50 border-green-300" 
                        : canTransmit 
                            ? "bg-blue-50 border-blue-300"
                            : "bg-slate-100 border-slate-300"
                )}>
                    <Checkbox
                        id="dossier-transmis"
                        checked={dossierTransmis}
                        onCheckedChange={handleTransmit}
                        disabled={saving || (!canTransmit && !dossierTransmis)}
                        data-testid="dossier-transmis-checkbox"
                    />
                    <div className="grid gap-1.5 leading-none">
                        <label htmlFor="dossier-transmis" className="text-sm font-medium leading-none cursor-pointer">
                            Dossier transmis au groupe WhatsApp
                        </label>
                        {transmissionInfo && (
                            <p className="text-xs text-green-600">
                                Transmis le {format(new Date(transmissionInfo.date), "dd/MM/yyyy 'à' HH:mm", { locale: fr })}
                                {transmissionInfo.user_name && ` par ${transmissionInfo.user_name}`}
                            </p>
                        )}
                    </div>
                </div>

                {!canTransmit && !dossierTransmis && (
                    <Alert variant="destructive" className="bg-red-50 border-red-200">
                        <Lock className="h-4 w-4" />
                        <AlertDescription>
                            Blocage : {!mandatConfirmed && "mandat non signé"}{!mandatConfirmed && facturesCount === 0 && " et "}{facturesCount === 0 && "aucune facture uploadée"}
                        </AlertDescription>
                    </Alert>
                )}

                {dossierTransmis && (
                    <Alert className="bg-amber-50 border-amber-200">
                        <Info className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-700">
                            <strong>Vous attendez l'étude.</strong> Téléversez-la ci-dessous dès réception.
                        </AlertDescription>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
}

// ============== ÉTAPE 5 : ÉTUDE ==============
function EtudeStep({ prospect, etudes, analyseClient, setAnalyseClient, onUpdate, isAdmin }) {
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');
    const fileInputRef = useRef(null);

    const handleUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        
        setUploading(true);
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('document_type', 'ETUDE');
                await api.post(`/prospects/${prospect.id}/documents`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
            toast.success('Étude(s) uploadée(s)');
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSaveAnalyse = async () => {
        setSaving(true);
        try {
            await api.put(`/prospects/${prospect.id}/easy-power-etude`, { etude: analyseClient });
            toast.success('Analyse enregistrée');
            onUpdate?.();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (documentId, filename) => {
        if (!window.confirm(`Voulez-vous vraiment supprimer "${filename}" ?`)) return;
        
        setDeleting(documentId);
        try {
            await api.delete(`/prospects/${prospect.id}/documents/${documentId}`);
            toast.success('Document supprimé');
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setDeleting(null);
        }
    };

    const handlePreview = async (document) => {
        try {
            const response = await api.get(`/prospects/${prospect.id}/documents/${document.id}/download`, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: document.mime_type || 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            setPreviewUrl(url);
            setPreviewFilename(document.original_filename);
        } catch (error) {
            toast.error('Erreur lors du chargement du document');
        }
    };

    const handleDownload = async (document) => {
        try {
            const response = await api.get(`/prospects/${prospect.id}/documents/${document.id}/download`, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = url;
            link.download = document.original_filename;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Erreur lors du téléchargement');
        }
    };

    const hasEtude = etudes.length > 0 || prospect?.easy_power_data?.etude_file_path;

    return (
        <Card className={cn(
            "border-2 transition-all",
            hasEtude ? "border-purple-300 bg-purple-50/50" : "border-slate-300 bg-slate-50/50"
        )} data-testid="step-etude">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                            hasEtude ? "bg-purple-500" : "bg-slate-400"
                        )}>
                            5
                        </div>
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <FileText className="w-5 h-5" />
                                Étude
                                {hasEtude && (
                                    <Badge className="bg-purple-100 text-purple-700">
                                        {etudes.length || 1} fichier(s)
                                    </Badge>
                                )}
                            </CardTitle>
                            <CardDescription>
                                Téléversez l'étude reçue et préparez le script client
                            </CardDescription>
                        </div>
                    </div>
                    <div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf"
                            onChange={handleUpload}
                            className="hidden"
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            size="sm"
                            variant="outline"
                            data-testid="upload-etude-btn"
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                            Ajouter étude
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Liste des études */}
                {(etudes.length > 0 || prospect?.easy_power_data?.etude_file_path) && (
                    <div className="space-y-2">
                        {etudes.map((etude) => (
                            <div key={etude.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                <div className="flex items-center gap-3">
                                    <FileText className="w-5 h-5 text-purple-600" />
                                    <span className="font-medium text-sm">{etude.original_filename}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handlePreview(etude)}
                                        data-testid={`preview-etude-${etude.id}`}
                                    >
                                        <Eye className="w-4 h-4 mr-1" /> Voir
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => handleDownload(etude)}
                                        data-testid={`download-etude-${etude.id}`}
                                    >
                                        <Download className="w-4 h-4" />
                                    </Button>
                                    {isAdmin && (
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => handleDelete(etude.id, etude.original_filename)}
                                            disabled={deleting === etude.id}
                                            data-testid={`delete-etude-${etude.id}`}
                                        >
                                            {deleting === etude.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                        {prospect?.easy_power_data?.etude_file_path && etudes.length === 0 && (
                            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <div className="flex items-center gap-3">
                                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                                    <div>
                                        <span className="font-medium text-sm">{prospect.easy_power_data.etude_filename || 'Document d\'étude (ancien)'}</span>
                                        <p className="text-xs text-amber-600">⚠️ Fichier de l'ancien système - veuillez re-uploader si nécessaire</p>
                                    </div>
                                </div>
                                <Badge className="bg-amber-100 text-amber-700">Ancien</Badge>
                            </div>
                        )}
                    </div>
                )}

                {/* Zone analyse / script client */}
                <div className="space-y-2 pt-4 border-t">
                    <Label className="flex items-center gap-2">
                        <ClipboardCheck className="w-4 h-4" />
                        Analyse / Script d'explication client
                    </Label>
                    <p className="text-xs text-slate-500">
                        Préparez le discours pour expliquer l'étude au client
                    </p>
                    <Textarea
                        value={analyseClient}
                        onChange={(e) => setAnalyseClient(e.target.value)}
                        placeholder="Points clés à présenter au client, économies potentielles, avantages du contrat..."
                        className="min-h-[120px]"
                        data-testid="analyse-client-textarea"
                    />
                    <div className="flex justify-end">
                        <Button
                            onClick={handleSaveAnalyse}
                            disabled={saving}
                            size="sm"
                            variant="outline"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                            Enregistrer
                        </Button>
                    </div>
                </div>
            </CardContent>

            {/* Dialog preview étude */}
            {previewUrl && (
                <Dialog open={!!previewUrl} onOpenChange={() => { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
                    <DialogContent className="max-w-4xl max-h-[90vh]">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-purple-600" />
                                {previewFilename}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="overflow-auto max-h-[70vh]">
                            <iframe src={previewUrl} className="w-full h-[65vh] border-0" title="Aperçu étude" />
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </Card>
    );
}

// ============== ÉTAPE 6 : PRÉSENTATION CLIENT ==============
function PresentationClientStep({ prospect, factures, etudes }) {
    const [slideshowOpen, setSlideshowOpen] = useState(false);
    const [slideshowIndex, setSlideshowIndex] = useState(0);
    const [activeTab, setActiveTab] = useState('factures');
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareLink, setShareLink] = useState(null);
    const [shareLoading, setShareLoading] = useState(false);
    const [shareLinks, setShareLinks] = useState([]);

    const hasDocuments = factures.length > 0 || etudes.length > 0 || prospect?.easy_power_data?.etude_file_path;

    // Combiner tous les documents pour le diaporama
    const allDocuments = [
        ...factures.map(f => ({
            ...f,
            document_type: 'FACTURE',
            prospect_id: prospect.id
        })),
        ...etudes.map(e => ({
            ...e,
            document_id: e.id,
            document_type: 'ETUDE',
            prospect_id: prospect.id
        }))
    ];

    // Charger les liens de partage existants
    const loadShareLinks = async () => {
        try {
            const res = await api.get(`/prospects/${prospect.id}/share-links`);
            setShareLinks(res.data || []);
        } catch (err) {
            console.error('Erreur chargement liens:', err);
        }
    };

    // Créer un lien de partage
    const createShareLink = async (options = {}) => {
        setShareLoading(true);
        try {
            const res = await api.post(`/prospects/${prospect.id}/share-link`, {
                expires_in_hours: options.expiresInHours || 48,
                include_factures: options.includeFactures !== false,
                include_etudes: options.includeEtudes !== false,
                client_name: options.clientName || null
            });
            setShareLink(res.data);
            toast.success('Lien de partage créé!');
            loadShareLinks();
        } catch (err) {
            toast.error('Erreur lors de la création du lien');
        } finally {
            setShareLoading(false);
        }
    };

    // Copier le lien
    const copyLink = () => {
        if (shareLink?.share_url) {
            navigator.clipboard.writeText(shareLink.share_url);
            toast.success('Lien copié!');
        }
    };

    // Révoquer un lien
    const revokeLink = async (linkId) => {
        try {
            await api.delete(`/prospects/${prospect.id}/share-links/${linkId}`);
            toast.success('Lien révoqué');
            loadShareLinks();
            if (shareLink?.id === linkId) {
                setShareLink(null);
            }
        } catch (err) {
            toast.error('Erreur lors de la révocation');
        }
    };

    // Ouvrir le diaporama
    const openSlideshow = (type, index = 0) => {
        if (type === 'factures') {
            setSlideshowIndex(index);
        } else {
            // Pour les études, l'index commence après les factures
            setSlideshowIndex(factures.length + index);
        }
        setSlideshowOpen(true);
    };

    // Télécharger un document
    const handleDownload = async (doc) => {
        try {
            let response;
            let filename;
            
            if (doc.document_type === 'ETUDE' && doc.document_id) {
                response = await api.get(`/prospects/${prospect.id}/documents/${doc.document_id}/download`, {
                    responseType: 'blob'
                });
                filename = doc.original_filename || doc.filename;
            } else if (doc.id) {
                response = await attachmentsAPI.download(doc.id);
                filename = doc.original_filename;
            }
            
            if (response) {
                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename || 'document';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            toast.error('Erreur lors du téléchargement');
        }
    };

    // Importer le composant Slideshow dynamiquement pour éviter les erreurs de build
    const DocumentSlideshow = require('./DocumentSlideshow').default;

    return (
        <Card className="border-2 border-cyan-300 bg-cyan-50/50" data-testid="step-presentation">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold">
                        6
                    </div>
                    <div className="flex-1">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Eye className="w-5 h-5" />
                            Présentation Client
                        </CardTitle>
                        <CardDescription>
                            Visualisez tous les documents pour présenter au client
                        </CardDescription>
                    </div>
                    {allDocuments.length > 0 && (
                        <>
                            <Button 
                                onClick={() => openSlideshow('factures', 0)}
                                className="bg-cyan-600 hover:bg-cyan-700"
                                data-testid="open-slideshow-btn"
                            >
                                <Eye className="w-4 h-4 mr-2" />
                                Diaporama ({allDocuments.length})
                            </Button>
                            <Button 
                                onClick={() => { setShareDialogOpen(true); loadShareLinks(); }}
                                variant="outline"
                                className="border-cyan-300 text-cyan-700 hover:bg-cyan-100"
                                data-testid="share-btn"
                            >
                                <Share2 className="w-4 h-4 mr-2" />
                                Partager
                            </Button>
                        </>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                {hasDocuments ? (
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="grid w-full grid-cols-2 mb-4">
                            <TabsTrigger value="factures" className="gap-2">
                                <Receipt className="w-4 h-4" />
                                Factures ({factures.length})
                            </TabsTrigger>
                            <TabsTrigger value="etude" className="gap-2">
                                <FileText className="w-4 h-4" />
                                Études ({etudes.length})
                            </TabsTrigger>
                        </TabsList>

                        <TabsContent value="factures">
                            <div className="space-y-2">
                                {factures.length > 0 ? (
                                    factures.map((facture, index) => (
                                        <div key={facture.id} className="flex items-center justify-between p-3 bg-white rounded-lg border hover:shadow-sm transition-shadow">
                                            <div className="flex items-center gap-3">
                                                <FileText className="w-5 h-5 text-amber-600" />
                                                <span className="text-sm font-medium">{facture.original_filename}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button 
                                                    variant="outline" 
                                                    size="sm" 
                                                    onClick={() => openSlideshow('factures', index)}
                                                    data-testid={`view-facture-${index}`}
                                                >
                                                    <Eye className="w-4 h-4 mr-1" /> Voir
                                                </Button>
                                                <Button variant="outline" size="sm" asChild>
                                                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${facture.id}/download`} download>
                                                        <Download className="w-4 h-4" />
                                                    </a>
                                                </Button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-6 text-slate-500">
                                        <Receipt className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                        <p className="text-sm">Aucune facture uploadée</p>
                                    </div>
                                )}
                            </div>
                        </TabsContent>

                        <TabsContent value="etude">
                            <div className="space-y-2">
                                {etudes.length > 0 ? (
                                    etudes.map((etude, index) => (
                                        <div key={etude.id} className="flex items-center justify-between p-3 bg-white rounded-lg border hover:shadow-sm transition-shadow">
                                            <div className="flex items-center gap-3">
                                                <FileText className="w-5 h-5 text-purple-600" />
                                                <span className="text-sm font-medium">{etude.original_filename}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button 
                                                    variant="outline" 
                                                    size="sm" 
                                                    onClick={() => openSlideshow('etude', index)}
                                                    data-testid={`view-etude-${index}`}
                                                >
                                                    <Eye className="w-4 h-4 mr-1" /> Voir
                                                </Button>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm" 
                                                    onClick={() => handleDownload({...etude, document_id: etude.id, document_type: 'ETUDE'})}
                                                >
                                                    <Download className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-6 text-slate-500">
                                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                                        <p className="text-sm">Aucune étude uploadée</p>
                                    </div>
                                )}
                                {prospect?.easy_power_data?.etude_file_path && etudes.length === 0 && (
                                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                                        <div className="flex items-center gap-3">
                                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                                            <div>
                                                <span className="text-sm font-medium">{prospect.easy_power_data.etude_filename || 'Étude (ancien système)'}</span>
                                                <p className="text-xs text-amber-600">Fichier de l'ancien système - veuillez re-uploader</p>
                                            </div>
                                        </div>
                                        <Badge className="bg-amber-100 text-amber-700">Ancien</Badge>
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                ) : (
                    <div className="text-center py-8 text-slate-500">
                        <Eye className="w-10 h-10 mx-auto mb-3 opacity-30" />
                        <p>Aucun document à présenter</p>
                        <p className="text-xs mt-1">Uploadez des factures et l'étude pour les visualiser ici</p>
                    </div>
                )}
            </CardContent>

            {/* Diaporama Documents */}
            <DocumentSlideshow
                documents={allDocuments}
                initialIndex={slideshowIndex}
                isOpen={slideshowOpen}
                onClose={() => setSlideshowOpen(false)}
                onDownload={handleDownload}
            />

            {/* Dialog de partage */}
            <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Share2 className="w-5 h-5 text-cyan-600" />
                            Partager la présentation
                        </DialogTitle>
                        <DialogDescription>
                            Créez un lien temporaire pour partager les documents avec votre client
                        </DialogDescription>
                    </DialogHeader>
                    
                    {!shareLink ? (
                        <div className="space-y-4">
                            <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                                <p className="text-sm text-cyan-800">
                                    <strong>Documents inclus:</strong> {factures.length} facture(s) et {etudes.length} étude(s)
                                </p>
                                <p className="text-xs text-cyan-600 mt-1">
                                    Le lien expire automatiquement après 12 heures
                                </p>
                            </div>
                            
                            <Button 
                                onClick={() => createShareLink({ expiresInHours: 12 })}
                                disabled={shareLoading || allDocuments.length === 0}
                                className="w-full bg-cyan-600 hover:bg-cyan-700"
                            >
                                {shareLoading ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création...</>
                                ) : (
                                    <><Link className="w-4 h-4 mr-2" /> Générer le lien de partage</>
                                )}
                            </Button>
                            
                            {/* Liens existants */}
                            {shareLinks.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="text-sm font-medium text-slate-600 mb-2">Liens actifs</h4>
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {shareLinks.filter(l => l.is_active).map(link => (
                                            <div key={link.id} className="flex items-center justify-between p-2 bg-slate-50 rounded border text-sm">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                    <span className="truncate text-slate-600">
                                                        Expire le {new Date(link.expires_at).toLocaleDateString('fr-FR')}
                                                    </span>
                                                    <Badge variant="outline" className="text-xs">{link.view_count} vues</Badge>
                                                </div>
                                                <div className="flex gap-1">
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost"
                                                        onClick={() => { setShareLink(link); }}
                                                    >
                                                        <Copy className="w-3 h-3" />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost"
                                                        className="text-red-500 hover:text-red-700"
                                                        onClick={() => revokeLink(link.id)}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                    <span className="font-medium text-green-800">Lien créé avec succès!</span>
                                </div>
                                <div className="flex items-center gap-2 mt-3">
                                    <Input 
                                        value={shareLink.share_url} 
                                        readOnly 
                                        className="text-sm bg-white"
                                    />
                                    <Button onClick={copyLink} variant="outline">
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                </div>
                                <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Expire le {new Date(shareLink.expires_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </p>
                            </div>
                            
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline" 
                                    className="flex-1"
                                    onClick={() => window.open(shareLink.share_url, '_blank')}
                                >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Ouvrir
                                </Button>
                                <Button 
                                    variant="outline"
                                    onClick={() => setShareLink(null)}
                                >
                                    Nouveau lien
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </Card>
    );
}

// ============== ÉTAPE 7 : RIB ==============
function RibUploadStep({ prospect, ribUploaded, onUpdate }) {
    const [uploading, setUploading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const fileInputRef = useRef(null);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (file.type !== 'application/pdf') {
            toast.error('Seuls les fichiers PDF sont acceptés');
            return;
        }
        
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            await api.post(`/prospects/${prospect.id}/easy-power-rib`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('RIB téléversé');
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const response = await prospectsAPI.downloadRib(prospect.id);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = prospect.easy_power_data?.rib_filename || 'rib.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('RIB téléchargé');
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors du téléchargement");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Card className={cn(
            "border-2 transition-all",
            ribUploaded ? "border-green-300 bg-green-50/50" : "border-emerald-300 bg-emerald-50/50"
        )} data-testid="step-rib">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                            ribUploaded ? "bg-green-500" : "bg-emerald-500"
                        )}>
                            7
                        </div>
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <CreditCard className="w-5 h-5" />
                                Upload RIB
                                {ribUploaded && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                            </CardTitle>
                            <CardDescription>
                                Le RIB est nécessaire pour l'édition du contrat
                            </CardDescription>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {ribUploaded && (
                            <>
                                <Button
                                    onClick={() => {
                                        const token = localStorage.getItem('valnexo_token');
                                        const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/prospects/${prospect.id}/easy-power/rib/view?token=${token}`;
                                        window.open(url, '_blank');
                                    }}
                                    size="sm"
                                    variant="outline"
                                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                    data-testid="view-rib-btn"
                                >
                                    <Eye className="w-4 h-4 mr-2" />
                                    Voir
                                </Button>
                                <Button
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    size="sm"
                                    variant="outline"
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                    data-testid="download-rib-btn"
                                >
                                    {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                                    Télécharger
                                </Button>
                            </>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf"
                            onChange={handleUpload}
                            className="hidden"
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            size="sm"
                            variant={ribUploaded ? "outline" : "default"}
                            data-testid="upload-rib-btn"
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                            {ribUploaded ? 'Remplacer' : 'Téléverser RIB'}
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {ribUploaded && prospect?.easy_power_data?.rib_filename && (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border mb-4">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-sm">{prospect.easy_power_data.rib_filename}</span>
                        <Badge className="bg-green-100 text-green-700">RIB</Badge>
                    </div>
                )}
                
                <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-700 text-sm">
                        <strong>Pensez à envoyer le RIB dans le groupe WhatsApp approprié</strong> pour permettre l'édition du contrat.
                        <br />
                        <span className="text-red-600">Sans RIB, aucun contrat ne peut être édité.</span>
                    </AlertDescription>
                </Alert>
            </CardContent>
        </Card>
    );
}

// ============== ÉTAPE 8 : SIGNATURE CONTRAT ==============
function SignatureContratStep({ prospectId, checklist, setChecklist, onUpdate }) {
    const [saving, setSaving] = useState(false);

    const handleCheckChange = async (field, checked) => {
        const newChecklist = { ...checklist, [field]: checked };
        setSaving(true);
        try {
            await api.put(`/prospects/${prospectId}/easy-power-checklist`, { checklist: newChecklist });
            setChecklist(newChecklist);
            onUpdate?.();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const allChecked = checklist.explique && checklist.correlation && checklist.signe;

    return (
        <Card className={cn(
            "border-2 transition-all",
            allChecked ? "border-emerald-400 bg-emerald-50/50" : "border-pink-300 bg-pink-50/50"
        )} data-testid="step-signature">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                    <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center text-white font-bold",
                        allChecked ? "bg-emerald-500" : "bg-pink-500"
                    )}>
                        8
                    </div>
                    <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <FileCheck className="w-5 h-5" />
                            Rappel Signature Contrat
                            {allChecked && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                        </CardTitle>
                        <CardDescription>
                            Checklist avant signature du contrat
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                        <Checkbox
                            id="check-explique"
                            checked={checklist.explique}
                            onCheckedChange={(checked) => handleCheckChange('explique', checked)}
                            disabled={saving}
                        />
                        <label htmlFor="check-explique" className="text-sm font-medium cursor-pointer">
                            Expliquer le contrat au client
                        </label>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                        <Checkbox
                            id="check-correlation"
                            checked={checklist.correlation}
                            onCheckedChange={(checked) => handleCheckChange('correlation', checked)}
                            disabled={saving}
                        />
                        <label htmlFor="check-correlation" className="text-sm font-medium cursor-pointer">
                            Montrer la corrélation avec l'étude
                        </label>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border">
                        <Checkbox
                            id="check-signe"
                            checked={checklist.signe}
                            onCheckedChange={(checked) => handleCheckChange('signe', checked)}
                            disabled={saving}
                        />
                        <label htmlFor="check-signe" className="text-sm font-medium cursor-pointer">
                            Faire signer le contrat
                        </label>
                    </div>
                </div>

                {allChecked && (
                    <Alert className="mt-4 bg-emerald-100 border-emerald-300">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        <AlertTitle className="text-emerald-700">Dossier complet !</AlertTitle>
                        <AlertDescription className="text-emerald-600">
                            Toutes les étapes ont été validées. Le contrat peut être finalisé.
                        </AlertDescription>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
}


// ============== SECTION GAZ (OPTIONNELLE) ==============
function GazSection({ prospectId, facturesGaz, etudesGaz, showGazSection, setShowGazSection, onUpdate }) {
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const fileInputFactureRef = useRef(null);
    const fileInputEtudeRef = useRef(null);
    const { user } = useAuth();
    
    const hasGazData = facturesGaz.length > 0 || etudesGaz.length > 0;

    const handleUpload = async (e, type) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        
        setUploading(true);
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('attachment_type', type); // FACTURE_GAZ ou ETUDE_GAZ
                await prospectsAPI.uploadAttachment(prospectId, formData);
            }
            toast.success(files.length > 1 ? `${files.length} fichiers ajoutés` : 'Fichier ajouté');
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Erreur lors de l'upload");
        } finally {
            setUploading(false);
            if (fileInputFactureRef.current) fileInputFactureRef.current.value = '';
            if (fileInputEtudeRef.current) fileInputEtudeRef.current.value = '';
        }
    };

    const handleDelete = async (attachmentId, attachments) => {
        const attachment = attachments.find(f => f.id === attachmentId);
        if (!attachment) return;
        
        const canDelete = (user?.role === 'admin_full' || user?.is_admin === true) || attachment.uploaded_by_id === user?.id;
        if (!canDelete) {
            toast.error('Seul l\'utilisateur ayant uploadé ce fichier peut le supprimer');
            return;
        }
        
        if (!window.confirm('Supprimer ce fichier ?')) return;
        
        setDeleting(attachmentId);
        try {
            await attachmentsAPI.delete(attachmentId);
            toast.success('Fichier supprimé');
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setDeleting(null);
        }
    };

    const handleDownload = async (attachment) => {
        try {
            const response = await attachmentsAPI.download(attachment.id);
            const blob = new Blob([response.data], { type: attachment.mime_type });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.original_filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Erreur lors du téléchargement');
        }
    };

    // Si pas de données gaz et section cachée, afficher juste le bouton d'ajout
    if (!showGazSection && !hasGazData) {
        return (
            <div className="flex justify-center">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowGazSection(true)}
                    className="border-dashed border-2 border-orange-300 text-orange-600 hover:bg-orange-50"
                    data-testid="add-gaz-section-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    <Flame className="w-4 h-4 mr-1" />
                    Ajouter une section Gaz (optionnel)
                </Button>
            </div>
        );
    }

    return (
        <Card className="border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50" data-testid="gaz-section">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                            <Flame className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                            <CardTitle className="text-lg text-orange-800 flex items-center gap-2">
                                Section Gaz
                                {hasGazData && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                            </CardTitle>
                            <CardDescription className="text-orange-600">
                                Factures et études gaz (optionnel)
                            </CardDescription>
                        </div>
                    </div>
                    {!hasGazData && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowGazSection(false)}
                            className="text-slate-400 hover:text-slate-600"
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Factures Gaz */}
                <div className="p-4 bg-white rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Receipt className="w-5 h-5 text-orange-600" />
                            <span className="font-medium">Factures Gaz</span>
                            {facturesGaz.length > 0 && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                    {facturesGaz.length}
                                </span>
                            )}
                        </div>
                        <div>
                            <input
                                ref={fileInputFactureRef}
                                type="file"
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => handleUpload(e, 'FACTURE_GAZ')}
                                className="hidden"
                            />
                            <Button
                                onClick={() => fileInputFactureRef.current?.click()}
                                disabled={uploading}
                                size="sm"
                                variant="outline"
                                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                data-testid="upload-facture-gaz-btn"
                            >
                                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                                Ajouter
                            </Button>
                        </div>
                    </div>
                    {facturesGaz.length > 0 && (
                        <div className="space-y-2">
                            {facturesGaz.map((facture) => (
                                <div 
                                    key={facture.id}
                                    className="flex items-center justify-between p-2 bg-orange-50 rounded-lg"
                                >
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-orange-600" />
                                        <span className="text-sm truncate max-w-[180px]">{facture.original_filename}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => {
                                                const token = localStorage.getItem('valnexo_token');
                                                const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/attachments/${facture.id}/view?token=${token}`;
                                                window.open(url, '_blank');
                                            }}
                                            title="Visualiser"
                                        >
                                            <Eye className="w-4 h-4 text-blue-600" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => handleDownload(facture)}>
                                            <Download className="w-4 h-4" />
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => handleDelete(facture.id, facturesGaz)}
                                            disabled={deleting === facture.id}
                                            className="text-red-600 hover:bg-red-50"
                                        >
                                            {deleting === facture.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Études Gaz */}
                <div className="p-4 bg-white rounded-lg border border-orange-200">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-orange-600" />
                            <span className="font-medium">Études Gaz</span>
                            {etudesGaz.length > 0 && (
                                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                    {etudesGaz.length}
                                </span>
                            )}
                        </div>
                        <div>
                            <input
                                ref={fileInputEtudeRef}
                                type="file"
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls"
                                onChange={(e) => handleUpload(e, 'ETUDE_GAZ')}
                                className="hidden"
                            />
                            <Button
                                onClick={() => fileInputEtudeRef.current?.click()}
                                disabled={uploading}
                                size="sm"
                                variant="outline"
                                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                data-testid="upload-etude-gaz-btn"
                            >
                                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                                Ajouter
                            </Button>
                        </div>
                    </div>
                    {etudesGaz.length > 0 && (
                        <div className="space-y-2">
                            {etudesGaz.map((etude) => (
                                <div 
                                    key={etude.id}
                                    className="flex items-center justify-between p-2 bg-orange-50 rounded-lg"
                                >
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-orange-600" />
                                        <span className="text-sm truncate max-w-[180px]">{etude.original_filename}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => {
                                                const token = localStorage.getItem('valnexo_token');
                                                const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/attachments/${etude.id}/view?token=${token}`;
                                                window.open(url, '_blank');
                                            }}
                                            title="Visualiser"
                                        >
                                            <Eye className="w-4 h-4 text-blue-600" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => handleDownload(etude)}>
                                            <Download className="w-4 h-4" />
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => handleDelete(etude.id, etudesGaz)}
                                            disabled={deleting === etude.id}
                                            className="text-red-600 hover:bg-red-50"
                                        >
                                            {deleting === etude.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
